import { useState, useMemo, useEffect } from 'react';
import { MembershipDto, VenueDto } from '@/services/platform/api/model';
import type { WhereSheetProps } from '../types';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupLabel,
  RadioGroupSupportText,
} from '@/components/ui/radio-group';
import { BookHubs } from '@/components/shared/booking/common/book/hubs';
import { Icon } from '@/components/ui/icon';
import { useLocation } from 'react-router-dom';
import { useLocaleStore } from '@/lib/core/stores';
import { getSevenRoomsVenues } from '@/services/platform/data/booking/sevenrooms';
import { getMembership } from '@/services/platform/data/profile';

export function WhereSheet({ isOpen, onClose, cmsProps, booking, onBookingChange }: WhereSheetProps) {
  const t = useClientTranslation();
  const location = useLocation();
  const locale = useLocaleStore(s => s.locale);
  const categoryId = (location.state as { categoryId?: string } | null)?.categoryId;

  const [venues, setVenues] = useState<VenueDto[]>([]);
  const ALL_HUB = t('App.Labels.All');

  useEffect(() => {
    getSevenRoomsVenues({ serviceCategoryNodeKey: categoryId }, locale).then(result => {
      setVenues(result ?? []);
    });
  }, [categoryId, locale]);

  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);

  useEffect(() => {
    getMembership().then(setMembership);
  }, []);

  const isMember = membership?.isActive === true;

  const isVenueMemberOnly = (venue: VenueDto) => venue.membersOnly ?? false;
  const isDisabled = (venue: VenueDto) => isVenueMemberOnly(venue) && !isMember;

  useEffect(() => {
    if (!venues.length) return;
    const enabled = venues.filter(v => !isDisabled(v));
    if (enabled.length !== 1) return;
    if (booking.venue?.venueNodeKey === enabled[0].venueNodeKey) return;
    onBookingChange({ venue: enabled[0], date: null, room: null, time: null });
    onClose();
  }, [venues, isMember]);

  const hubs = useMemo(() => {
    const locations = venues.map(v => v.location).filter((l): l is string => !!l);
    return [ALL_HUB, ...Array.from(new Set(locations))];
  }, [venues]);

  const [activeHub, setActiveHub] = useState(ALL_HUB);

  const filtered = useMemo(
    () => (activeHub === ALL_HUB ? venues : venues.filter(v => v.location === activeHub)),
    [venues, activeHub]
  );

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <div
          data-base-ui-swipe-ignore
          className="flex flex-col gap-[32px] overflow-y-auto px-[16px] pt-[32px] pb-[100px]"
        >
          <DrawerHeader className="gap-2 px-0">
            <div className="flex items-center gap-8">
              <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
                {cmsProps?.title ?? t('App.Labels.Where')}
              </DrawerTitle>
              <DrawerClose
                className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </DrawerClose>
            </div>
            {cmsProps?.subtitle && (
              <DrawerDescription className="text-[16px] text-[#707070]">
                {cmsProps.subtitle}
              </DrawerDescription>
            )}
          </DrawerHeader>

          <BookHubs hubs={hubs} activeHub={activeHub} onHubChange={setActiveHub} />

          {/* Venue list */}
          <RadioGroup
            value={booking.venue?.venueNodeKey ?? ''}
            onValueChange={key => {
              const venue = filtered.find(v => v.venueNodeKey === key);
              if (venue) {
                onBookingChange({ venue, date: null, room: null, time: null });
                onClose();
              }
            }}
            className="flex flex-col"
          >
            {filtered.map(venue => {
              const disabled = isDisabled(venue);
              const itemId = `where-${venue.venueNodeKey}`;

              return (
                <RadioGroupLabel
                  key={venue.venueNodeKey}
                  htmlFor={itemId}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 border-b-[0.5px] border-[#e1e1e1] py-[24px] text-[20px] tracking-normal text-[#1a1a1a]',
                    disabled && 'pointer-events-none opacity-40'
                  )}
                >
                  <RadioGroupItem id={itemId} value={venue.venueNodeKey ?? ''} disabled={disabled} />

                  <div className="flex flex-col items-start gap-1">
                    <span className="font-pp-neue-montreal font-normal">{venue.name}</span>
                    {disabled && (
                      <RadioGroupSupportText className="rounded-[2px] bg-[#f4f4f4] px-0 py-0.5 text-[11px] font-medium tracking-[0.5px] text-[#9a9a9a] uppercase">
                        {t('App.Labels.MembersOnly')}
                      </RadioGroupSupportText>
                    )}
                  </div>
                </RadioGroupLabel>
              );
            })}
          </RadioGroup>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
